/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useCallback, useEffect, useRef, useState } from 'react'

import { getCommonHeaders } from '@/lib/api'

import { API_ENDPOINTS, ERROR_MESSAGES } from '../constants'
import {
  isStreamDoneMessage,
  parseSseEvents,
  parseStreamErrorDetails,
  parseStreamErrorMessage,
  parseStreamMessageUpdates,
} from '../lib'
import type { ChatCompletionRequest } from '../types'

export function useStreamRequest() {
  const activeRequestRef = useRef<AbortController | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)

  const stopStream = useCallback(() => {
    const activeRequest = activeRequestRef.current
    activeRequestRef.current = null
    activeRequest?.abort()
    setIsStreaming(false)
  }, [])

  useEffect(
    () => () => {
      const activeRequest = activeRequestRef.current
      activeRequestRef.current = null
      activeRequest?.abort()
    },
    []
  )

  const sendStreamRequest = useCallback(
    (
      payload: ChatCompletionRequest,
      onUpdate: (type: 'reasoning' | 'content', chunk: string) => void,
      onComplete: () => void,
      onError: (error: string, errorCode?: string) => void
    ) => {
      stopStream()
      const controller = new AbortController()
      activeRequestRef.current = controller
      setIsStreaming(true)

      const isActive = () => activeRequestRef.current === controller
      const finish = () => {
        if (!isActive()) return
        activeRequestRef.current = null
        controller.abort()
        setIsStreaming(false)
      }
      const fail = (message: string, code?: string) => {
        if (!isActive()) return
        finish()
        onError(message, code)
      }

      void (async () => {
        try {
          const response = await fetch(API_ENDPOINTS.CHAT_COMPLETIONS, {
            method: 'POST',
            headers: getCommonHeaders(),
            body: JSON.stringify(payload),
            credentials: 'same-origin',
            signal: controller.signal,
          })
          if (!isActive()) return

          if (!response.ok) {
            const details = parseStreamErrorDetails(await response.text())
            fail(
              `HTTP ${response.status}: ${details.errorMessage}`,
              details.errorCode
            )
            return
          }
          if (!response.body) {
            fail(ERROR_MESSAGES.CONNECTION_CLOSED)
            return
          }

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''
          let receivedOutput = false
          let completed = false

          const processEvents = (input: string) => {
            const result = parseSseEvents(input)
            buffer = result.rest
            for (const event of result.events) {
              if (!isActive()) return
              if (!event.data.trim()) continue
              if (isStreamDoneMessage(event.data)) {
                completed = true
                if (!receivedOutput) {
                  fail(ERROR_MESSAGES.API_REQUEST_ERROR)
                } else {
                  finish()
                  onComplete()
                }
                return
              }

              const streamError = parseStreamErrorMessage(event.data)
              if (event.event === 'error' || streamError) {
                const details =
                  streamError ?? parseStreamErrorDetails(event.data)
                fail(details.errorMessage, details.errorCode)
                return
              }

              for (const update of parseStreamMessageUpdates(event.data)) {
                if (update.chunk.trim()) receivedOutput = true
                onUpdate(update.type, update.chunk)
              }
            }
          }

          while (isActive()) {
            const { value, done } = await reader.read()
            if (done) break
            processEvents(buffer + decoder.decode(value, { stream: true }))
            if (completed || !isActive()) return
          }

          if (!isActive()) return
          processEvents(`${buffer}${decoder.decode()}\n\n`)
          if (!completed && isActive()) fail(ERROR_MESSAGES.CONNECTION_CLOSED)
        } catch (error: unknown) {
          if (!isActive()) return
          // eslint-disable-next-line no-console
          console.error('Playground stream failed:', error)
          fail(ERROR_MESSAGES.NETWORK_ERROR)
        }
      })()
    },
    [stopStream]
  )

  return { sendStreamRequest, stopStream, isStreaming }
}
